// frontend/src/pages/MyP2PTrades.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import p2pAxios from "../p2pAxios";

const PAGE_SIZE = 25;
const COIN_DECIMALS = { USDT: 6, USDC: 6, BTC: 8, ETH: 8 };

const STATUS_OPTIONS = [
  "all",
  "initiated",
  "paid",
  "released",
  "completed",
  "disputed",
  "cancelled",
  "cancelled_after_paid",
  "expired",
];

const fmtINR = (n) => Number(n || 0).toLocaleString("en-IN");
const toNum = (v) => Number(v || 0);
const coinDp = (c) => COIN_DECIMALS[(c || "USDT").toUpperCase()] ?? 8;
const fmtCoin = (n, coin) => toNum(n).toFixed(coinDp(coin));
const normStatus = (s) => String(s || "").toLowerCase();

const getStatusBadge = (status) => {
  const s = normStatus(status);
  switch (s) {
    case "completed":
      return "bg-green-100 text-green-700 dark:bg-green-900/35 dark:text-green-200";
    case "paid":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/35 dark:text-yellow-200";
    case "released":
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/35 dark:text-blue-200";
    case "disputed":
      return "bg-red-100 text-red-700 dark:bg-red-900/35 dark:text-red-200";
    case "cancelled":
      return "bg-gray-200 text-gray-700 dark:bg-slate-800 dark:text-gray-200";
    case "initiated":
      return "bg-orange-100 text-orange-700 dark:bg-orange-900/35 dark:text-orange-200";
    case "cancelled_after_paid":
    case "canceled_after_paid":
      return "bg-gray-200 text-gray-800 dark:bg-slate-800 dark:text-gray-100";
    case "expired":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/35 dark:text-purple-200";
    default:
      return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  }
};

const formatDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

export default function MyP2PTrades() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1); // 1-based
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("date_desc"); // date_desc | date_asc | status_asc | status_desc
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMyTrades = async () => {
      try {
        const res = await p2pAxios.get("/trades/my-trades");
        setTrades(res.data.trades || []);
      } catch (err) {
        console.error("Failed to fetch trades:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMyTrades();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, sortKey]);

  const filteredSortedTrades = useMemo(() => {
    let rows = trades;

    // Filter by status
    if (statusFilter !== "all") {
      rows = rows.filter((t) => normStatus(t?.status) === statusFilter);
    }

    // Sort
    const byDate = (a, b) => {
      const da = new Date(a?.createdAt || a?.updatedAt || a?.date || 0).getTime();
      const db = new Date(b?.createdAt || b?.updatedAt || b?.date || 0).getTime();
      return da - db;
    };

    const byStatus = (a, b) => {
      const sa = normStatus(a?.status);
      const sb = normStatus(b?.status);
      if (sa < sb) return -1;
      if (sa > sb) return 1;
      return 0;
    };

    const out = [...rows];
    switch (sortKey) {
      case "date_asc":
        out.sort(byDate);
        break;
      case "status_asc":
        out.sort(byStatus);
        break;
      case "status_desc":
        out.sort((a, b) => -byStatus(a, b));
        break;
      case "date_desc":
      default:
        out.sort((a, b) => -byDate(a, b));
        break;
    }
    return out;
  }, [trades, statusFilter, sortKey]);

  const total = filteredSortedTrades.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, total);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const visibleTrades = useMemo(
    () => filteredSortedTrades.slice(startIdx, endIdx),
    [filteredSortedTrades, startIdx, endIdx]
  );

  const counterpartyName = (t) => {
    const cp = t?.counterparty;
    if (!cp) return "User";
    if (cp.username) return `@${cp.username}`;
    const full = `${cp.firstName || ""} ${cp.lastName || ""}`.trim();
    return full || "User";
  };

  const showType = (t) => String(t?.type || "-").toLowerCase();
  const goToPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const prettyStatus = (s) =>
    s === "all" ? "All" : s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="w-full">
      {/* Mobile-friendly scroll container:
          - If this component is inside a fixed-height hub, it will scroll.
          - If it's a normal page, the browser scroll handles it naturally. */}
      <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6">
        <div className="mt-3 md:mt-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
          {/* Header */}
          <div className="px-3 sm:px-4 md:px-5 py-3 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-[15px] sm:text-base md:text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                  My P2P Trades
                </h2>
                {!loading && (
                  <p className="mt-0.5 text-[11px] sm:text-[12px] text-slate-500 dark:text-slate-400">
                    Showing <span className="font-medium text-slate-700 dark:text-slate-200">{total ? startIdx + 1 : 0}</span>–
                    <span className="font-medium text-slate-700 dark:text-slate-200">{endIdx}</span> of{" "}
                    <span className="font-medium text-slate-700 dark:text-slate-200">{total}</span>
                  </p>
                )}
              </div>

              {/* Filters toggle (compact) */}
              <button
                onClick={() => setShowFilters((v) => !v)}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-[12px] font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900"
              >
                <span>{showFilters ? "Hide" : "Filters"}</span>
                <span className="text-slate-400">•</span>
                <span className="text-slate-500 dark:text-slate-400">
                  {prettyStatus(statusFilter)}
                </span>
              </button>
            </div>

            {/* Filters panel (hidden by default) */}
            {showFilters && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-[13px] text-slate-800 dark:text-slate-100"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {prettyStatus(s)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">
                    Sort By
                  </label>
                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-[13px] text-slate-800 dark:text-slate-100"
                  >
                    <option value="date_desc">Date: Newest First</option>
                    <option value="date_asc">Date: Oldest First</option>
                    <option value="status_asc">Status: A → Z</option>
                    <option value="status_desc">Status: Z → A</option>
                  </select>
                </div>

                <div className="sm:col-span-2 flex items-center justify-end gap-2">
                  <button
                    onClick={() => {
                      setStatusFilter("all");
                      setSortKey("date_desc");
                    }}
                    className="rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2 text-[12px] font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="rounded-lg bg-slate-900 dark:bg-white px-3 py-2 text-[12px] font-semibold text-white dark:text-slate-900 hover:opacity-90"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="px-3 sm:px-4 md:px-5 py-3">
            {loading ? (
              <p className="text-[13px] text-slate-500 dark:text-slate-400">Loading trades...</p>
            ) : total === 0 ? (
              <p className="text-[13px] text-slate-600 dark:text-slate-300">
                No trades found.
              </p>
            ) : (
              <>
                {/* ===== Mobile: Compact card list ===== */}
                <div className="md:hidden space-y-2">
                  {visibleTrades.map((t) => {
                    const coin = (t?.coin || "USDT").toUpperCase();
                    const when = t?.createdAt || t?.updatedAt || t?.date;

                    return (
                      <button
                        key={t._id}
                        onClick={() => navigate(`/p2p/trade/${t._id}`)}
                        className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900 px-3 py-2.5"
                      >
                        {/* Top row */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                                #{String(t._id).slice(-6)}
                              </span>
                              <span className="text-[11px] text-slate-400">•</span>
                              <span className="text-[11px] text-slate-600 dark:text-slate-300 truncate">
                                {counterpartyName(t)}
                              </span>
                            </div>
                            <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                              {formatDateTime(when)}
                            </div>
                          </div>

                          <span
                            className={`shrink-0 px-2 py-0.5 rounded-md text-[11px] font-semibold ${getStatusBadge(
                              t?.status
                            )}`}
                          >
                            {normStatus(t?.status)}
                          </span>
                        </div>

                        {/* Details row */}
                        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-slate-700 dark:text-slate-200">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-500 dark:text-slate-400">Type</span>
                            <span className="capitalize font-medium">{showType(t)}</span>
                          </div>

                          <div className="flex items-center justify-between gap-2 tabular-nums">
                            <span className="text-slate-500 dark:text-slate-400">Amount</span>
                            <span className="font-medium">
                              {fmtCoin(t?.amount, coin)} {coin}
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-2 tabular-nums">
                            <span className="text-slate-500 dark:text-slate-400">Fiat</span>
                            <span className="font-medium">₹{fmtINR(t?.fiatAmount)}</span>
                          </div>

                          <div className="flex items-center justify-between gap-2 tabular-nums">
                            <span className="text-slate-500 dark:text-slate-400">Price</span>
                            <span className="font-medium">₹{fmtINR(t?.price)}</span>
                          </div>
                        </div>

                        <div className="mt-2 text-[12px] font-semibold text-slate-900 dark:text-slate-100">
                          View Trade →
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* ===== Desktop: Clean table (still scalable) ===== */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full table-auto text-sm text-left">
                    <thead className="bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2">Trade ID</th>
                        <th className="px-3 py-2">Date &amp; Time</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">Coin</th>
                        <th className="px-3 py-2">Amount</th>
                        <th className="px-3 py-2">Fiat (INR)</th>
                        <th className="px-3 py-2">Price (₹/coin)</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Counterparty</th>
                        <th className="px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleTrades.map((t) => {
                        const coin = (t?.coin || "USDT").toUpperCase();
                        const when = t?.createdAt || t?.updatedAt || t?.date;

                        return (
                          <tr
                            key={t._id}
                            className="border-b border-slate-200 dark:border-slate-800 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-900"
                          >
                            <td className="px-3 py-2 text-xs font-mono text-slate-600 dark:text-slate-300">
                              {String(t._id).slice(-6)}
                            </td>
                            <td className="px-3 py-2">{formatDateTime(when)}</td>
                            <td className="px-3 py-2 capitalize">{showType(t)}</td>
                            <td className="px-3 py-2">{coin}</td>
                            <td className="px-3 py-2 tabular-nums">
                              {fmtCoin(t?.amount, coin)} {coin}
                            </td>
                            <td className="px-3 py-2 tabular-nums">₹{fmtINR(t?.fiatAmount)}</td>
                            <td className="px-3 py-2 tabular-nums">₹{fmtINR(t?.price)}</td>
                            <td className="px-3 py-2">
                              <span
                                className={`px-2 py-0.5 rounded-md text-xs font-semibold ${getStatusBadge(
                                  t?.status
                                )}`}
                              >
                                {normStatus(t?.status)}
                              </span>
                            </td>
                            <td className="px-3 py-2">{counterpartyName(t)}</td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => navigate(`/p2p/trade/${t._id}`)}
                                className="text-slate-900 dark:text-white font-semibold hover:underline"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination (compact + phone-friendly) */}
                <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    className={`rounded-lg border px-3 py-2 text-[12px] font-semibold ${
                      page <= 1
                        ? "border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                        : "border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900"
                    }`}
                  >
                    Prev
                  </button>

                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => goToPage(p)}
                        className={`min-w-[2.1rem] rounded-lg border px-2.5 py-2 text-[12px] font-semibold ${
                          p === page
                            ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white"
                            : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900"
                        }`}
                        aria-current={p === page ? "page" : undefined}
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages}
                    className={`rounded-lg border px-3 py-2 text-[12px] font-semibold ${
                      page >= totalPages
                        ? "border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed"
                        : "border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-900"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Extra bottom padding so it doesn't feel cramped on phones (and won't clash with bottom nav if you add it) */}
        <div className="h-20 md:h-6" />
      </div>
    </div>
  );
}
